import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useSalesInvoiceService from '../../../app/services/hooks/useSalesInvoiceService';
import useSocietyClientService from '../../../app/services/hooks/useSocietyClientService';
import useDeliveryService from '../../../app/services/hooks/useDeliveryService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { SalesInvoiceDataForm, defaultSalesInvoiceDataForm } from '../../../app/models/SalesInvoice';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { getTodayLocalDateString } from '../../../helpers';

import SalesInvoiceForm from './forms/SalesInvoiceForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const SalesInvoiceCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    
    const { 
        createSalesInvoice, 
        fetchingCreateSalesInvoice,
        storeSalesInvoice, 
        fetchingStoreSalesInvoice 
    } = useSalesInvoiceService();

    const {
        getSocietyClients,
        fetchingGetSocietyClients
    } = useSocietyClientService();

    const {
        getDeliveriesBySocietyClient,
        fetchingGetDeliveriesBySocietyClient
    } = useDeliveryService();

    // Estados
    const [salesInvoice, setSalesInvoice] = useState<SalesInvoiceDataForm>({
        ...defaultSalesInvoiceDataForm,
        issue_date: getTodayLocalDateString(),
        payment_statuses_id: []
    });
    const [errorFields, setErrorFields] = useState<any>();
    const [societies, setSocieties] = useState<any[]>([]);
    const [clients, setClients] = useState<any[]>([]);
    const [deliveries, setDeliveries] = useState<any[]>([]);

    // Cargar datos iniciales (sociedades)
    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = () => {
        console.log('🔄 Cargando datos iniciales...');
        createSalesInvoice({
            onSuccess: (response: ServiceResponse) => {
                console.log('✅ Respuesta del servidor:', response);
                console.log('📊 Sociedades recibidas:', response.data?.societies);
                setSocieties(response.data?.societies || []);
                
                if (!response.data?.societies || response.data.societies.length === 0) {
                    toast.warning('No hay sociedades activas disponibles');
                }
            },
            onError: (response: ServiceResponse) => {
                console.error('❌ Error al cargar datos iniciales:', response);
                toast.error('Error al cargar datos iniciales: ' + response.message);
            }
        });
    };

    // Cargar clientes cuando cambia la sociedad
    const handleSocietyChange = (societyId: number | string) => {
        setClients([]);
        setDeliveries([]);
        
        if (!societyId) return;

        getSocietyClients(
            {
                society_id: Number(societyId),
                page: 1,
                per_page: 1000, // Traer todos los clientes
                sort: 'id',
                order: 'asc'
            }, 
            {
                onSuccess: (response: ServiceResponse) => {
                    console.log('📊 Society Clients recibidos:', response.data?.society_clients);
                    
                    // Extraer los clientes del objeto society_client
                    const clientsData = response.data?.society_clients?.map((sc: any) => ({
                        id: sc.client?.id,
                        name: sc.client?.business_name || sc.client?.name || 'Sin nombre',
                        business_name: sc.client?.business_name
                    })) || [];
                    
                    console.log('✅ Clientes procesados:', clientsData);
                    setClients(clientsData);
                    
                    if (clientsData.length === 0) {
                        toast.info('No hay clientes asociados a esta sociedad');
                    }
                },
                onError: (response: ServiceResponse) => {
                    console.error('❌ Error al cargar clientes:', response);
                    toast.error('Error al cargar clientes: ' + response.message);
                }
            }
        );
    };

    // Cargar entregas cuando cambia el cliente
    const handleClientChange = (clientId: number | string) => {
        setDeliveries([]);
        
        if (!salesInvoice.society_id || !clientId) return;

        getDeliveriesBySocietyClient(Number(salesInvoice.society_id), Number(clientId), {
            onSuccess: (response: ServiceResponse) => {
                setDeliveries(response.data?.deliveries || []);
                
                if (!response.data?.deliveries || response.data.deliveries.length === 0) {
                    toast.info('No hay entregas disponibles para facturar', { autoClose: 3000 });
                }
            },
            onError: (response: ServiceResponse) => {
                toast.error('Error al cargar entregas: ' + response.message);
            }
        });
    };

    const store = () => {
        // Validación básica
        if (!salesInvoice.society_id) {
            toast.error('Debe seleccionar una sociedad');
            return;
        }

        if (!salesInvoice.client_id) {
            toast.error('Debe seleccionar un cliente');
            return;
        }

        if (!salesInvoice.issue_date) {
            toast.error('Debe ingresar la fecha de emisión');
            return;
        }

        if (!salesInvoice.payment_statuses_id || salesInvoice.payment_statuses_id.length === 0) {
            toast.error('Debe seleccionar al menos una entrega para facturar');
            return;
        }

        if (showLoading) showLoading('loading', 'Guardando factura de venta...');

        storeSalesInvoice(salesInvoice, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                toast.success(response.message, { autoClose: 2500 });

                // Navegar directamente al edit de la factura creada
                if (response.data?.invoice_id) {
                    navigationPage('/sales-invoices/' + response.data.invoice_id + '/edit');
                    return;
                }

                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
                toast.error('Por favor, corrija los errores en el formulario');
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return (
        <>
            {fetchingCreateSalesInvoice ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-2">Cargando datos iniciales...</p>
                </div>
            ) : (
                <>
                    <div className="row">
                        <div className="col-12">
                            <div className="alert alert-info">
                                <i className="mdi mdi-information"></i> Complete los siguientes datos para crear una nueva factura de venta. 
                                Seleccione las entregas que desea incluir en esta factura.
                            </div>
                            
                            <SalesInvoiceForm
                                salesInvoice={salesInvoice}
                                setSalesInvoice={setSalesInvoice}
                                errorFields={errorFields}
                                societies={societies}
                                clients={clients}
                                deliveries={deliveries}
                                onSocietyChange={handleSocietyChange}
                                onClientChange={handleClientChange}
                                loadingClients={fetchingGetSocietyClients}
                                loadingDeliveries={fetchingGetDeliveriesBySocietyClient}
                            />
                        </div>
                    </div>
                    
                    <hr />
                    
                    <div className="row justify-content-end">
                        <div className="col-auto">
                            <ButtonCancelForm 
                                callbackCancel={cancel} 
                                locked={fetchingStoreSalesInvoice} 
                            />
                        </div>
                        <div className="col-auto">
                            <ButtonSaveForm 
                                callbackSave={store} 
                                locked={fetchingStoreSalesInvoice} 
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default SalesInvoiceCreate;
